import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../contexts/BookingContext';
import { useLanguage } from '../contexts/LanguageContext';
import ProgressBar from '../components/ProgressBar';
import { workspaceOptions } from '../data/mockData';

const WorkspacePage: React.FC = () => {
  const navigate = useNavigate();
  const { setWorkspace } = useBooking();
  const { language } = useLanguage();
  const t = language === 'zh-TW' ? zhTW : enUS;

  const [selectedOption, setSelectedOption] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // 篩選狀態
  const [filters, setFilters] = useState({
    priceRange: 'all',
    wifiSpeed: 'all',
    hasMeeting: 'all',
    hours: 'all',
  });

  const handleFilterChange = (filterType: string, value: any) => {
    setFilters({ ...filters, [filterType]: value });
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleOptionSelect = (option: any) => {
    setSelectedOption(option);
  };

  const handleNext = () => {
    if (selectedOption) {
      setWorkspace(selectedOption);
      navigate('/community');
    }
  };

  const handleSkip = () => {
    navigate('/community');
  };

  // 篩選工作空間
  const filteredWorkspaces = workspaceOptions.filter(workspace => {
    const matchesPrice = filters.priceRange === 'all' || 
      (filters.priceRange === 'budget' && workspace.pricing.daily <= 200) ||
      (filters.priceRange === 'mid' && workspace.pricing.daily > 200 && workspace.pricing.daily <= 400) ||
      (filters.priceRange === 'premium' && workspace.pricing.daily > 400);

    const matchesWifi = filters.wifiSpeed === 'all' ||
      (filters.wifiSpeed === 'slow' && parseInt(workspace.features.wifi) <= 50) ||
      (filters.wifiSpeed === 'medium' && parseInt(workspace.features.wifi) > 50 && parseInt(workspace.features.wifi) <= 150) ||
      (filters.wifiSpeed === 'fast' && parseInt(workspace.features.wifi) > 150);

    const matchesMeeting = filters.hasMeeting === 'all' ||
      (filters.hasMeeting === 'yes' && workspace.features.meeting) ||
      (filters.hasMeeting === 'no' && !workspace.features.meeting);

    const matchesHours = filters.hours === 'all' ||
      (filters.hours === '24h' && workspace.features.hours === '24/7') ||
      (filters.hours === 'day' && workspace.features.hours !== '24/7');

    const matchesSearch = workspace.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      workspace.description.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesPrice && matchesWifi && matchesMeeting && matchesHours && matchesSearch;
  });

  // 計算分頁
  const totalPages = Math.ceil(filteredWorkspaces.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentWorkspaces = filteredWorkspaces.slice(startIndex, endIndex);

  const clearFilters = () => {
    setFilters({
      priceRange: 'all',
      wifiSpeed: 'all',
      hasMeeting: 'all',
      hours: 'all',
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== 'all') || searchTerm.trim() !== '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <ProgressBar currentStep={4} />
        
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            {t.workspace.title}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t.workspace.subtitle}
          </p>
        </div>

        {/* 篩選器區域 */}
        <div className="bg-white rounded-2xl shadow-soft p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">篩選條件</h2>
          
          {/* 搜尋框 */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              🔍 {t.workspace.search}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder={t.workspace.searchPlaceholder}
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300"
              />
              <button
                onClick={() => setCurrentPage(1)}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 font-semibold shadow-medium hover:shadow-strong"
              >
                🔍 {t.workspace.search}
              </button>
            </div>
          </div>

          {/* 篩選選項 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* 價格範圍 */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                💰 {t.workspace.priceRange}
              </label>
              <select
                value={filters.priceRange}
                onChange={(e) => handleFilterChange('priceRange', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300"
              >
                <option value="all">{t.workspace.allPrices}</option>
                <option value="budget">{t.workspace.budget}</option>
                <option value="mid">{t.workspace.midRange}</option>
                <option value="premium">{t.workspace.premium}</option>
              </select>
            </div>

            {/* WiFi速度 */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                📶 {t.workspace.wifiSpeed}
              </label>
              <select
                value={filters.wifiSpeed}
                onChange={(e) => handleFilterChange('wifiSpeed', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300"
              >
                <option value="all">{t.workspace.allSpeeds}</option>
                <option value="slow">{t.workspace.slow}</option>
                <option value="medium">{t.workspace.medium}</option>
                <option value="fast">{t.workspace.fast}</option>
              </select>
            </div>

            {/* 會議室 */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                🏢 {t.workspace.meetingRoom}
              </label>
              <select
                value={filters.hasMeeting}
                onChange={(e) => handleFilterChange('hasMeeting', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300"
              >
                <option value="all">{t.workspace.all}</option>
                <option value="yes">{t.workspace.yes}</option>
                <option value="no">{t.workspace.no}</option>
              </select>
            </div>

            {/* 營業時間 */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                ⏰ {t.workspace.operatingHours}
              </label>
              <select
                value={filters.hours}
                onChange={(e) => handleFilterChange('hours', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300"
              >
                <option value="all">{t.workspace.allHours}</option>
                <option value="24h">{t.workspace.hours24}</option>
                <option value="day">{t.workspace.dayHours}</option>
              </select>
            </div>
          </div>

          {/* 清除篩選按鈕 */}
          {hasActiveFilters && (
            <div className="flex justify-end">
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors duration-300 font-medium"
              >
                🗑️ {t.workspace.clearFilters}
              </button>
            </div>
          )}
        </div>

        {/* 推薦結果 */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold text-gray-800">
              推薦工作空間 ({filteredWorkspaces.length} 個選項)
            </h2>
            {hasActiveFilters && (
              <span className="text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                🔍 已套用篩選條件
              </span>
            )}
          </div>
        </div>

        {/* 工作空間列表 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {currentWorkspaces.map((workspace, index) => (
            <div
              key={workspace.id}
              onClick={() => handleOptionSelect(workspace)}
              className={`bg-white rounded-2xl shadow-soft overflow-hidden cursor-pointer transition-all duration-300 transform hover:scale-105 hover:shadow-medium relative ${
                selectedOption?.id === workspace.id
                  ? 'ring-4 ring-blue-200'
                  : 'hover:bg-gray-50'
              }`}
            >
              {/* 推薦標籤 */}
              {startIndex + index < 2 && (
                <div className="absolute top-4 left-4 z-10">
                  <span className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-medium">
                    {startIndex + index === 0 ? '🥇 最推薦' : '🥈 推薦'}
                  </span>
                </div>
              )}

              <div className="relative">
                <img
                  src={workspace.image}
                  alt={workspace.name}
                  className="w-full h-48 object-cover"
                />
                <div className="absolute top-4 right-4">
                  <span className="bg-white text-gray-800 px-3 py-1 rounded-full text-sm font-semibold shadow-soft">
                    {workspace.price}
                  </span>
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {workspace.name}
                </h3>
                <p className="text-gray-600 mb-4">{workspace.description}</p>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full">
                    📶 {workspace.features.wifi}
                  </span>
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full">
                    🪑 {workspace.features.desks} {t.workspace.desks}
                  </span>
                  {workspace.features.coffee && (
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 text-sm rounded-full">
                      ☕ {t.workspace.coffee}
                    </span>
                  )}
                  {workspace.features.meeting && (
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full">
                      🏢 {t.workspace.meeting}
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap gap-1">
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                    ⏰ {workspace.features.hours}
                  </span>
                </div>

                {selectedOption?.id === workspace.id && (
                  <div className="mt-4 text-center">
                    <div className="inline-flex items-center text-blue-600 font-semibold">
                      <span className="mr-2">✓</span>
                      已選擇
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* 分頁控制 */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center space-x-2 mb-8">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-xl transition-colors duration-300 ${
                currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              ← {t.workspace.previous}
            </button>
            
            <div className="flex space-x-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-2 rounded-lg transition-colors duration-300 ${
                    currentPage === page
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded-xl transition-colors duration-300 ${
                currentPage === totalPages
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              {t.workspace.next} →
            </button>
          </div>
        )}

        {/* 分頁資訊 */}
        {totalPages > 1 && (
          <div className="text-center text-gray-600 mb-6">
            顯示第 {startIndex + 1} - {Math.min(endIndex, filteredWorkspaces.length)} 項，
            共 {filteredWorkspaces.length} 項結果
          </div>
        )}

        {/* 導航按鈕 */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          {selectedOption && (
            <button
              onClick={handleNext}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-3 px-8 rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-medium"
            >
              {t.common.next}
            </button>
          )}
          
          <button
            onClick={handleSkip}
            className="w-full sm:w-auto bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-8 rounded-2xl transition-all duration-300"
          >
            我不需要工作空間
          </button>
        </div>

        {selectedOption && (
          <div className="mt-8 bg-blue-50 rounded-2xl p-6 max-w-2xl mx-auto">
            <h3 className="text-lg font-semibold text-blue-800 mb-2">
              已選擇：{selectedOption.name}
            </h3>
            <p className="text-blue-600">
              我們將為您安排 {selectedOption.price} 的工作空間，
              提供 {selectedOption.features.wifi} WiFi 和 {selectedOption.features.desks} 個座位
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// 中文翻譯
const zhTW = {
  workspace: {
    title: '選擇工作空間',
    subtitle: '選擇適合您的工作環境，從安靜的圖書館到熱鬧的創業空間，應有盡有',
    search: '搜尋',
    searchPlaceholder: '搜尋工作空間名稱或描述...',
    priceRange: '價格範圍',
    allPrices: '所有價格',
    budget: '經濟型 (≤NT$200)',
    midRange: '中價位 (NT$200-400)',
    premium: '高級 (≥NT$400)',
    wifiSpeed: 'WiFi速度',
    allSpeeds: '所有速度',
    slow: '慢速 (≤50Mbps)',
    medium: '中速 (50-150Mbps)',
    fast: '高速 (≥150Mbps)',
    meetingRoom: '會議室',
    all: '全部',
    yes: '有',
    no: '無',
    operatingHours: '營業時間',
    allHours: '所有時間',
    hours24: '24小時',
    dayHours: '日間營業',
    clearFilters: '清除篩選',
    foundResults: '找到 {count} 個工作空間',
    pageInfo: '第 {current} 頁，共 {total} 頁',
    previous: '上一頁',
    next: '下一頁',
    desks: '座位',
    coffee: '咖啡',
    meeting: '會議室',
    hours: '營業時間'
  },
  common: {
    back: '返回',
    next: '下一步'
  }
};

// 英文翻譯
const enUS = {
  workspace: {
    title: 'Choose Workspace',
    subtitle: 'Select the perfect work environment, from quiet libraries to vibrant startup spaces',
    search: 'Search',
    searchPlaceholder: 'Search workspace name or description...',
    priceRange: 'Price Range',
    allPrices: 'All Prices',
    budget: 'Budget (≤NT$200)',
    midRange: 'Mid-range (NT$200-400)',
    premium: 'Premium (≥NT$400)',
    wifiSpeed: 'WiFi Speed',
    allSpeeds: 'All Speeds',
    slow: 'Slow (≤50Mbps)',
    medium: 'Medium (50-150Mbps)',
    fast: 'Fast (≥150Mbps)',
    meetingRoom: 'Meeting Room',
    all: 'All',
    yes: 'Yes',
    no: 'No',
    operatingHours: 'Operating Hours',
    allHours: 'All Hours',
    hours24: '24 Hours',
    dayHours: 'Day Hours',
    clearFilters: 'Clear Filters',
    foundResults: 'Found {count} workspaces',
    pageInfo: 'Page {current} of {total}',
    previous: 'Previous',
    next: 'Next',
    desks: 'desks',
    coffee: 'Coffee',
    meeting: 'Meeting',
    hours: 'Hours'
  },
  common: {
    back: 'Back',
    next: 'Next'
  }
};

export default WorkspacePage; 